import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Wallet } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { periodQuery, trainersReportQuery, useApprovePeriod, useReopenPeriod, type Period, type PeriodLine } from '@/api/payroll'
import { PayoutStateBadge, StatusChip } from '@/app/payroll/badges'
import { MonthBar, PayrollShell, type PayrollContext } from '@/app/payroll/payroll-shell'
import { PersonPanel } from '@/app/payroll/person-panel'
import { ReasonDialog } from '@/app/payroll/reason-dialog'
import { UnmarkedPanel } from '@/app/payroll/unmarked-panel'
import { wallOf } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { formatMoney } from '@/domain/pass-rules'
import { approvableFrom, approveVerdict, balanceText, balanceTone, dayTime, signedMoney, sortLines } from '@/domain/payroll'
import { payrollErrorText } from '@/domain/payroll-errors'
import { monthLabel, monthOf } from '@/domain/services'
import { formatDate, t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Checkbox } from '@/ui/checkbox'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'
import { StatTile, StatTiles } from '@/ui/stat-tile'

type Search = { month?: string }

export const Route = createFileRoute('/_app/c/$orgId/payroll/')({
  validateSearch: (search: Record<string, unknown>): Search =>
    typeof search['month'] === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(search['month']) ? { month: search['month'] } : {},
  component: Sheet,
})

function Sheet() {
  const { orgId } = Route.useParams()
  return (
    <PayrollShell orgId={orgId} tab="sheet">
      {(ctx) => <SheetScreen ctx={ctx} />}
    </PayrollShell>
  )
}

/// «Відомість» — one month of the organisation (specs/payroll-periods.md
/// §4): per person за заняття + оклад + коригування = нараховано, what was
/// paid and what is left, live until «Затвердити» freezes it. A row opens the
/// person; the month is approved here and, with a reason, reopened.
function SheetScreen({ ctx }: { ctx: PayrollContext }) {
  const p = t.payroll
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const thisMonth = monthOf(wallOf(new Date(), ctx.zone).date)
  const month = search.month ?? thisMonth
  const period = useQuery(periodQuery(ctx.orgId, month, ctx.zone))
  const report = useQuery(trainersReportQuery(ctx.orgId, month, ctx.zone, ctx.can.schedule))
  const [person, setPerson] = useState<string | null>(null)
  const [unmarkedOpen, setUnmarkedOpen] = useState(false)

  const go = (next: string) => void navigate({ search: next === thisMonth ? {} : { month: next }, replace: true })
  const toolbar = <MonthBar month={month} max={thisMonth} onChange={go} />

  if (period.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {toolbar}
        <LoadingBlock label={t.app.loading} />
      </div>
    )
  }
  if (period.isError) {
    return (
      <div className="flex flex-col gap-6">
        {toolbar}
        <InlineBanner kind="error" message={errorText(period.error, payrollErrorText())} action={{ label: t.app.retry, onClick: () => void period.refetch() }} />
      </div>
    )
  }

  const data = period.data
  const approved = data.status === 'approved'
  const held = new Map((report.data?.trainers ?? []).map((row) => [row.trainerUserId, row.held]))
  const lines = sortLines(data.lines, ctx.nameOf)
  const opened = person ? (data.lines.find((line) => line.userId === person) ?? null) : null

  const columns: readonly DataColumn<PeriodLine>[] = [
    {
      key: 'person',
      header: p.columns.person,
      width: 'minmax(176px,2fr)',
      cell: (line) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={ctx.nameOf(line.userId)} src={ctx.photoOf(line.userId)} className="size-7" />
          <span className="truncate">{ctx.nameOf(line.userId)}</span>
        </span>
      ),
    },
    ...(ctx.can.schedule
      ? [
          {
            key: 'held',
            header: p.columns.held,
            width: '96px',
            numeric: true,
            hideBelow: 'xl',
            mobile: 'hidden',
            cell: (line: PeriodLine) => (report.data ? (held.get(line.userId) ?? 0) : '—'),
          } satisfies DataColumn<PeriodLine>,
        ]
      : []),
    { key: 'sessions', header: p.columns.sessions, width: 'minmax(88px,0.8fr)', numeric: true, hideBelow: 'md', mobile: 'hidden', cell: (line) => money(line.sessionsMinor) },
    { key: 'salary', header: p.columns.salary, width: 'minmax(88px,0.8fr)', numeric: true, hideBelow: 'lg', mobile: 'hidden', cell: (line) => money(line.salaryMinor) },
    {
      key: 'adjustments',
      header: p.columns.adjustments,
      width: 'minmax(88px,0.8fr)',
      numeric: true,
      hideBelow: 'xl',
      mobile: 'hidden',
      cell: (line) => (line.adjustmentsMinor === 0 ? <span className="text-muted">—</span> : signedMoney(line.adjustmentsMinor)),
    },
    {
      key: 'total',
      header: p.columns.total,
      width: 'minmax(88px,0.8fr)',
      numeric: true,
      mobile: 'subtitle',
      cell: (line) => <span className="font-medium text-text">{formatMoney(line.totalMinor)}</span>,
    },
    { key: 'paid', header: p.columns.paid, width: 'minmax(88px,0.8fr)', numeric: true, hideBelow: 'md', mobile: 'hidden', cell: (line) => money(line.paidMinor) },
    {
      key: 'balance',
      header: p.columns.balance,
      width: 'minmax(88px,0.8fr)',
      numeric: true,
      mobile: 'trailing',
      cell: (line) => <span className={cn('font-medium', toneText[balanceTone(line.balanceMinor)])}>{balanceText(line.balanceMinor)}</span>,
    },
    { key: 'state', header: p.columns.state, width: '112px', hideBelow: 'md', mobile: 'hidden', cell: (line) => <PayoutStateBadge state={line.payoutState} /> },
  ]

  return (
    <div className={cn('flex flex-col gap-8', period.isPlaceholderData && 'opacity-60 transition-opacity')}>
      <div className="flex flex-col gap-3">
        {toolbar}
        <StatusLine ctx={ctx} period={data} />
      </div>

      {data.changesAfterApproval.length > 0 ? <ChangesBanner ctx={ctx} period={data} /> : null}

      {/* While the month runs, `planned` also counts the sessions still ahead —
          the banner speaks once the month is over and they are all past. */}
      {data.isOver && data.unmarked.count > 0 ? (
        <InlineBanner
          kind="warning"
          title={p.unmarked.title(data.unmarked.count)}
          message={p.unmarked.message}
          action={{ label: p.unmarked.open, onClick: () => setUnmarkedOpen(true) }}
        />
      ) : null}

      {ctx.can.manage ? <ApproveBar ctx={ctx} period={data} /> : null}

      {data.lines.length === 0 ? (
        <EmptyState icon={<Wallet />} title={p.empty} message={p.emptyMessage} />
      ) : (
        <>
          <StatTiles className="grid-cols-2 lg:grid-cols-4">
            <StatTile label={approved ? p.tiles.approved : p.tiles.accrued} value={formatMoney(data.totals.totalMinor)} note={breakdown(data)} />
            <StatTile label={p.tiles.paid} value={formatMoney(data.totals.paidMinor)} />
            <StatTile
              label={p.tiles.balance}
              value={balanceText(data.totals.balanceMinor)}
              tone={data.totals.balanceMinor > 0 ? 'warning' : data.totals.balanceMinor < 0 ? 'danger' : 'normal'}
            />
            <StatTile label={p.tiles.people} value={String(data.totals.people)} />
          </StatTiles>

          <section className="flex flex-col gap-2">
            <SectionHeader title={p.tabs.sheet} count={lines.length} aside={ctx.can.schedule ? <span className="hidden lg:inline">{p.heldNote}</span> : undefined} />
            <DataList
              label={p.tabs.sheet}
              columns={columns}
              rows={lines}
              rowKey={(line) => line.userId}
              rowTarget={(line) => ({ onClick: () => setPerson(line.userId) })}
              rowLabel={(line) => p.open(ctx.nameOf(line.userId))}
            />
            {data.scope === 'branches' ? <p className="px-4 text-caption text-muted">{p.branchScopeNote}</p> : null}
          </section>
        </>
      )}

      <AuditList ctx={ctx} period={data} />

      <PersonPanel ctx={ctx} period={data} line={opened} onClose={() => setPerson(null)} />
      <UnmarkedPanel ctx={ctx} period={data} open={unmarkedOpen} onClose={() => setUnmarkedOpen(false)} />
    </div>
  )
}

const toneText = {
  normal: 'text-text',
  warning: 'text-warning',
  danger: 'text-danger',
  success: 'text-success',
  muted: 'text-text-2',
} as const

const money = (minor: number): ReactNode => (minor === 0 ? <span className="text-muted">—</span> : formatMoney(minor))

/// «за заняття 12 400 ₴ · оклад 15 000 ₴ · коригування +500 ₴».
const breakdown = (period: Period): string => {
  const p = t.payroll.tiles
  return [
    `${t.payroll.tiles.sessionsShort} ${formatMoney(period.totals.sessionsMinor)}`,
    period.totals.salaryMinor ? `${t.payroll.tiles.salaryShort} ${formatMoney(period.totals.salaryMinor)}` : null,
    period.totals.adjustmentsMinor ? `${p.adjustments.toLowerCase()} ${signedMoney(period.totals.adjustmentsMinor)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/// «Відкритий» or «Затверджено» — who and when, how many times it was reopened.
const StatusLine = ({ ctx, period }: { ctx: PayrollContext; period: Period }) => {
  const s = t.payroll.status
  const approved = period.status === 'approved'
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-1">
      <StatusChip tone={approved ? 'success' : 'normal'}>{approved ? s.approved : s.open}</StatusChip>
      <span className="text-caption text-text-2 text-pretty">
        {approved && period.approvedAt && period.approvedBy
          ? s.approvedBy(ctx.nameOf(period.approvedBy), dayTime(period.approvedAt, period.timeZone))
          : approved
            ? s.approvedNote
            : s.openNote}
        {period.reopenedCount > 0 ? ` · ${s.reopened(period.reopenedCount)}` : ''}
      </span>
    </div>
  )
}

/// «Затвердити» with what stands in its way (PP3, PP4), or «Відкрити знову»
/// with a reason (PP5). `payroll = manage`, company scope.
function ApproveBar({ ctx, period }: { ctx: PayrollContext; period: Period }) {
  const a = t.payroll.approve
  const approve = useApprovePeriod(ctx.orgId)
  const reopen = useReopenPeriod(ctx.orgId)
  const [accept, setAccept] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const verdict = approveVerdict(period, accept)
  const label = monthLabel(period.month)

  if (period.status === 'approved') {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-[16px] bg-surface px-4 py-3">
        <p className="min-w-0 flex-1 text-caption text-text-2 text-pretty">{t.payroll.status.approvedNote}</p>
        <Button onClick={() => setReopening(true)}>{a.reopen}</Button>
        <ReasonDialog
          open={reopening}
          onOpenChange={(next) => {
            setReopening(next)
            if (!next) setError(null)
          }}
          title={a.reopenTitle(label)}
          message={a.reopenMessage}
          confirmLabel={a.reopen}
          placeholder={a.reasonPlaceholder}
          destructive={false}
          busy={reopen.isPending}
          error={error}
          onConfirm={(reason) =>
            reopen.mutate(
              { month: period.month, reason },
              { onSuccess: () => setReopening(false), onError: (failure) => setError(errorText(failure, payrollErrorText('period'))) },
            )
          }
        />
      </div>
    )
  }

  const why = !verdict.allowed
    ? verdict.reason === 'not_over'
      ? a.notOver(formatDate(approvableFrom(period.month)))
      : verdict.reason === 'unmarked'
        ? a.unmarkedFirst
        : null
    : null

  return (
    <div className="flex flex-col gap-2 rounded-[16px] bg-surface px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
      <div className="flex min-w-0 flex-1 flex-col">
        {period.isOver && period.unmarked.count > 0 ? (
          <Checkbox checked={accept} onCheckedChange={setAccept}>
            {t.payroll.unmarked.accept}
          </Checkbox>
        ) : null}
        {why ? <p id="approve-why" className="text-caption text-text-2 text-pretty">{why}</p> : null}
      </div>
      <Button variant="primary" disabled={!verdict.allowed} aria-describedby={why ? 'approve-why' : undefined} onClick={() => setConfirming(true)}>
        {a.action}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={(next) => {
          setConfirming(next)
          if (!next) setError(null)
        }}
        title={a.confirmTitle(label)}
        message={
          <>
            {a.confirmMessage}
            {period.unmarked.count > 0 ? ` ${a.confirmUnmarked(period.unmarked.count)}` : ''}
          </>
        }
        confirmLabel={a.action}
        destructive={false}
        busy={approve.isPending}
        error={error ? <InlineBanner kind="error" message={error} /> : undefined}
        onConfirm={() =>
          approve.mutate(
            { month: period.month, acceptUnmarked: accept, timeZone: ctx.zone },
            {
              onSuccess: () => {
                setConfirming(false)
                setAccept(false)
              },
              onError: (failure) => setError(errorText(failure, payrollErrorText('period'))),
            },
          )
        }
      />
    </div>
  )
}

/// «Зміни після затвердження» (PP2): per person, approved → live.
const ChangesBanner = ({ ctx, period }: { ctx: PayrollContext; period: Period }) => {
  const c = t.payroll.changes
  return (
    <InlineBanner
      kind="stale"
      title={c.title}
      message={
        <span className="flex flex-col gap-0.5">
          <span>{c.message}</span>
          {period.changesAfterApproval.map((change) => (
            <span key={change.userId} className="font-medium text-text tabular">
              {c.line(ctx.nameOf(change.userId), formatMoney(change.approvedTotalMinor), formatMoney(change.liveTotalMinor), signedMoney(change.deltaMinor))}
            </span>
          ))}
        </span>
      }
    />
  )
}

/// The month's approvals and reopenings, newest first.
const AuditList = ({ ctx, period }: { ctx: PayrollContext; period: Period }) => {
  const a = t.payroll.audit
  if (period.audit.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader title={a.title} count={period.audit.length} />
      <ul className="divide-y divide-surface-3 overflow-hidden rounded-[16px] bg-surface">
        {period.audit.map((row) => {
          const total = row.totals?.['totalMinor']
          const words = [
            dayTime(row.at, period.timeZone),
            typeof total === 'number' ? a.total(formatMoney(total)) : null,
            row.acceptedUnmarked > 0 ? a.acceptedUnmarked(row.acceptedUnmarked) : null,
          ].filter(Boolean)
          return (
            <li key={row.id} className="flex flex-col px-4 py-3">
              <span className="text-callout font-medium text-text">
                {row.action === 'approved' ? a.approved(ctx.nameOf(row.byUserId)) : a.reopened(ctx.nameOf(row.byUserId))}
              </span>
              <span className="text-caption text-text-2 tabular">{words.join(' · ')}</span>
              {row.reason ? <span className="text-caption text-text-2 text-pretty">«{row.reason}»</span> : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
